import React from 'react';
import PropTypes from 'prop-types';
import css from 'styled-jsx/css';
import cx from 'classnames';
import { Heading as DesignHeading } from '@lmig/lmds-react';
import breakpoints from '@lmig/lmds-tokens/dist/json/core/breakpoint.json';

const getEyebrowStyles = ({ eyebrow }) => {
  if (eyebrow) {
    return css.resolve`
      .lm-Heading {
        position: relative;

        &::before {
          content: '';
          position: absolute;
          width: 5rem;
          height: 0.375rem;
          background-color: #ffd000;
          top: -2rem;
          border-radius: 0.25rem;
        }
      }
    `;
  }

  return { className: '', styles: '' };
};

const getMarginTopStyles = ({ marginTop }) => {
  if (marginTop === 'none') {
    return css.resolve`
      .lm-Heading {
        margin-top: 0;
      }
    `;
  }

  return { className: '', styles: '' };
};

const getMarginBottomStyles = ({ marginBottom }) => {
  if (marginBottom === 'none') {
    return css.resolve`
      .lm-Heading {
        margin-bottom: 0;
      }
    `;
  }

  return { className: '', styles: '' };
};

const getAlignStyles = ({ align }) => {
  switch (align) {
    case 'center':
      return css.resolve`
        .lm-Heading {
          max-width: none;
        }
      `;
    case 'right':
      return css.resolve`
        .lm-Heading {
          max-width: none;
        }
      `;
    default:
      return { className: '', styles: '' };
  }
};

const getColorStyles = ({ onBackground }) => {
  switch (onBackground) {
    case 'yellow':
      return css.resolve`
        .lm-Heading {
          color: #1a1446;
        }
      `;
    case 'blue':
      return css.resolve`
        .lm-Heading {
          color: #fff;
        }
      `;
      case 'white':
        return css.resolve`
          .lm-Heading {
            color: #06748C;
            font-weight: 400;
          }
        `;
    default:
      return { className: '', styles: '' };
  }
};

const getMobileHeading = (mobileHeadingCentered, headingWidth, mobileMargin) => {
  if (mobileHeadingCentered) {
    return css.resolve`

    .lm-Heading {
      text-align: center;
    }
    @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
      .lm-Heading {
        text-align: left;
      }
    }
    `;
  }

  if (headingWidth) {
    return css.resolve`

    .lm-Heading {
      margin: 0 auto;
      padding-bottom: 1rem;
    }
    @media screen and (min-width: 22.8rem) and (max-width: 37.4rem) {
      .lm-Heading {
        width: 19rem;
        font-size: 2.5rem;
      }
    }
    @media screen and (min-width: 37.5rem) and (max-width: 47.9rem){
      .lm-Heading {
        width: 24rem;
      }
    }
    @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
      .lm-Heading {
        text-align: center
      }
    }
    `;
  }

  if (mobileMargin) {
    return css.resolve`

    .lm-Heading {
      @media screen and (min-width: 22.8rem) and (max-width: 47.9rem) {
        margin-top: 0;
      }
    }
    `;
  }

  return { className: '', styles: '' };
};

const { className: superscriptClass, styles: superscriptStyles } = css.resolve`
  * :global(sup) {
    line-height: 0;
  }
`;

const getColorRoundedStyles =  (colorRounded)  => {
  if (colorRounded) {
    return css.resolve`

    .lm-Heading {
      background-color: #f2fcfc;
      border-radius: 1.5rem;
      width: 22rem;
      height: 5rem;
      padding-top: 1.5rem;

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.sm.min}) {
        width: 28rem;
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
        width: 19rem;
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min}) {
        width: 25rem;
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.xl.min}) {
        width: 33.5rem;
      }
    }
    `;
  }
      
  return { className: '', styles: '' };
  
};

const getPaddingStyles = (padding, paddingLeftRight) => {
  if (padding) {
    return css.resolve`

    .lm-Heading {
      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
       padding: 0.75rem;
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min}) {
       padding: 1.5rem 0 0 0;
      }
    }
    `;
  }

  if (paddingLeftRight) {
    return css.resolve`

    .lm-Heading {
      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
       padding: 0 4rem;
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.lg.min}) {
       padding: 0;
      }
    }
    `;
  }

  return { className: '', styles: '' };

};

const getHeadingStyles = (headingStyle) => {
  if (headingStyle) {
    return css.resolve`

    .lm-Heading {
      color: #343741;

      @media screen and (max-width: 48rem) {
       font-size: 3rem;
      }

      @media screen and (min-width: ${breakpoints.lmig.breakpoint.md.min}) {
       font-size: 2.25rem;
      }
    }
    `;
  }

  return { className: '', styles: '' };

};

const Heading = ({
  className = '',
  eyebrow = '',
  marginTop = '',
  marginBottom = '',
  align = 'left',
  onBackground = 'whites',
  mobileHeadingCentered = false,
  headingWidth = false,
  mobileMargin = false,
  colorRounded = false,
  padding = false,
  headingStyle = false,
  paddingLeftRight = false,
  children,
  ...rest
}) => {
  const { className: eyebrowClass, styles: eyebrowStyles } = getEyebrowStyles({ eyebrow });
  const { className: marginTopClass, styles: marginTopStyles } = getMarginTopStyles({ marginTop });
  const { className: marginBottomClass, styles: marginBottomStyles } = getMarginBottomStyles({
    marginBottom,
  });
  const { className: alignClass, styles: alignStyles } = getAlignStyles({ align });
  const { className: colorClass, styles: colorStyles } = getColorStyles({ onBackground });
  const { className: mobileClass, styles: mobileStyles } = getMobileHeading(mobileHeadingCentered, headingWidth, mobileMargin);
  const { className: colorRoundedClass, styles: colorRoundedStyles } = getColorRoundedStyles(colorRounded);
  const { className: paddingClass, styles: paddingStyles } = getPaddingStyles(padding, paddingLeftRight);
  const { className: headingClass, styles: headingStyles } = getHeadingStyles(headingStyle);





  const classNameModified = cx(
    className,
    eyebrowClass,
    marginTopClass,
    marginBottomClass,
    alignClass,
    colorClass,
    superscriptClass,
    mobileClass,
    colorRoundedClass,
    paddingClass,
    headingClass
  );

  return (
    <>
      <DesignHeading align={align} className={classNameModified} {...rest}>
        {children}
      </DesignHeading>
      {eyebrowStyles}
      {marginTopStyles}
      {marginBottomStyles}
      {alignStyles}
      {colorStyles}
      {superscriptStyles}
      {mobileStyles}
      {colorRoundedStyles}
      {paddingStyles}
      {headingStyles}
    </>
  );
};

Heading.contentRender = ({ text, children, ...rest }) => <Heading {...rest}>{children || text}</Heading>;

Heading.propTypes = {
  /** Pass in other style classes */
  className: PropTypes.string,
  /** Turn on and off Eyebrow above heading...It's a yellow line */
  eyebrow: PropTypes.oneOf(['', 'libertyYellow']),
  /** Adds and removes top margin of heading */
  marginTop: PropTypes.oneOf(['', 'none']),
  /** Adds and removes bottom margin of heading */
  marginBottom: PropTypes.oneOf(['', 'none']),
  /** Align heading  */
  align: PropTypes.oneOf([undefined, 'center', 'right', 'left']),
  /** Color the text based upon background color */
  onBackground: PropTypes.oneOf([undefined, 'yellow', 'blue', 'whites']),
  mobileHeadingCentered: PropTypes.bool,
  headingWidth: PropTypes.bool,
  mobileMargin: PropTypes.bool,
  colorRounded: PropTypes.bool,
  padding: PropTypes.bool,
  paddingLeftRight: PropTypes.bool
};

export default Heading;
